import { useState } from 'react';
import type { Audit } from '../../lib/api';
import { Button } from '../../components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Archive, ArchiveRestore, Pencil, Loader2, MoreVertical, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

/** Resolves null on success, or the reason it failed. */
type AuditMutation = () => Promise<string | null>;

interface AuditArchiveMenuProps {
  /** A list row or the open audit (the detail extends the row). */
  audit: Audit;
  /** Offer "Edit details" first (the audit list's row menu). */
  onEdit?: () => void;
  /** Archive / Restore / Delete (customer admins). Default true: the header only renders this for admins. */
  canArchive?: boolean;
  /** Smaller trigger for table rows. */
  compact?: boolean;
  onArchive: AuditMutation;
  onRestore: AuditMutation;
  /** Resolves null once the audit is gone (the page then leaves). */
  onDelete: AuditMutation;
}

type Confirming = 'archive' | 'delete' | null;

/**
 * The audit header's "More" menu for customer admins: Archive (or Restore) and,
 * while the audit is still in progress, Delete. Archive and Delete confirm
 * first and keep a failure inside the dialog. The server re-checks every one.
 */
export default function AuditArchiveMenu({
  audit, onArchive, onRestore, onDelete, onEdit, canArchive = true, compact = false,
}: AuditArchiveMenuProps) {
  const [confirming, setConfirming] = useState<Confirming>(null);
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const canDelete = audit.status === 'in_progress' && !audit.archived;

  const ask = (what: Exclude<Confirming, null>) => { setError(null); setConfirming(what); };

  const confirm = async () => {
    if (!confirming) return;
    setBusy(true);
    setError(null);
    const failure = await (confirming === 'archive' ? onArchive() : onDelete());
    setBusy(false);
    if (failure) setError(failure);
    else setConfirming(null);
  };

  const restore = async () => {
    setBusy(true);
    const failure = await onRestore();
    setBusy(false);
    if (failure) toast.error(failure);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant={compact ? 'ghost' : 'outline'} size="icon" className={compact ? 'h-9 w-9' : 'h-11 w-11'}
            disabled={busy} aria-label="More audit actions">
            {busy && !confirming ? <Loader2 className="size-5 animate-spin" /> : <MoreVertical className="size-5" />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          {onEdit && (
            <DropdownMenuItem className="py-2.5" onSelect={onEdit}>
              <Pencil className="h-4 w-4 mr-2" />Edit details
            </DropdownMenuItem>
          )}
          {onEdit && canArchive && <DropdownMenuSeparator />}
          {canArchive && (audit.archived ? (
            <DropdownMenuItem className="py-2.5" onSelect={() => void restore()}>
              <ArchiveRestore className="h-4 w-4 mr-2" />Restore audit
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem className="py-2.5" onSelect={() => ask('archive')}>
              <Archive className="h-4 w-4 mr-2" />Archive audit
            </DropdownMenuItem>
          ))}
          {canArchive && canDelete && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="py-2.5 text-destructive focus:text-destructive" onSelect={() => ask('delete')}>
                <Trash2 className="h-4 w-4 mr-2" />Delete audit
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirming !== null} onOpenChange={(open) => { if (!open && !busy) setConfirming(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirming === 'delete' ? 'Delete audit' : 'Archive audit'}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirming === 'delete'
                ? "Delete this audit permanently? Its answers, photos, actions and notes are removed. This can't be undone."
                : "Archive this audit? It disappears from the audit lists, the supplier's profile, reminders and the calendar. You can restore it later."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          <AlertDialogFooter>
            <AlertDialogCancel className="h-11" disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={busy}
              className={`h-11 ${confirming === 'delete' ? 'bg-destructive text-white hover:bg-destructive/90' : ''}`}
              onClick={(e) => { e.preventDefault(); void confirm(); }}>
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {confirming === 'delete' ? 'Delete audit' : 'Archive audit'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
